import { Widget, WidgetSize } from '@automation-suite/shared-clients-widget-variant';
import { type MessageConsumer, type MessageProducer } from '@automation-suite/shared-clients-window-connection';
import { logger } from '@automation-suite/shared-clients-widget-logger';
import { isIOSDevice } from '@automation-suite/shared-utils-helpers';

import {
  defaultIframeStyles,
  getDesktopDefaultStyles,
  getDesktopSupportWindowStyles,
  getMobileDefaultStyles,
  getMobileSupportWindowStyles,
  GetStylesParams,
} from './consts';
import {
  type Configs,
  type HostToIframeMessage,
  type IframeToHostMessage,
  type Options,
  ActionMethod,
  type Actions,
  type DisplayModes,
  ShowActionDisplayModeSchema,
  type TransitionMap,
} from './types';
import { getDisplayModeToResponsiveStyles } from './utils';
import { createTransitionMap } from './utils';
import { resolvePosition } from './utils/resolvePosition';

// Keyboard counts as dismissed once the inset drops below this share of its peak (device-agnostic vs a fixed px).
const KEYBOARD_CLOSED_INSET_RATIO = 0.5;
// Delay before locking scroll so iOS finishes its focus auto-scroll; locking late drops the card below the keyboard.
const IOS_FOCUS_SETTLE_MS = 70;
// Ignore the noisy URL-bar + keyboard-open transient after focus, else a transient false-reads as "closed".
const KEYBOARD_OPEN_SETTLE_MS = 500;

export class WidgetVariantFloatingInputCard extends Widget<
  Actions,
  Configs,
  Options,
  MessageConsumer<IframeToHostMessage>,
  MessageProducer<HostToIframeMessage>
> {
  private previouslyVisibleDisplayMode: DisplayModes | undefined;
  private _currentDisplayMode: DisplayModes = 'HIDDEN';
  private processingQueue = false;
  private transitionQueue: (() => Promise<void>)[] = [];
  private transitionMap: TransitionMap | undefined;
  private mediaQueryListeners: Array<() => void> = [];
  private keyboardFillCleanup?: () => void;
  private currentKeyboardInsetPx = 0;
  private maxKeyboardInsetPx = 0;
  private currentBaseHeightPx?: number;
  private inputFocused = false;
  private keyboardOpenGuardUntil = 0;
  private parentScrollLocked = false;
  private focusTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  get currentDisplayMode() {
    return this._currentDisplayMode;
  }

  set currentDisplayMode(value: DisplayModes) {
    if (this._currentDisplayMode !== 'HIDDEN') {
      this.previouslyVisibleDisplayMode = this._currentDisplayMode;
    }
    this._currentDisplayMode = value;
  }

  public getIframeStyles(isDesktop: boolean): Partial<CSSStyleDeclaration> {
    return isDesktop
      ? this.displayModeToResponsiveStyles[this.currentDisplayMode].desktop
      : this.displayModeToResponsiveStyles[this.currentDisplayMode].mobile;
  }

  public async beforeMount() {
    this.iframeToHostMessageConsumer?.addListener('transition-to-fab', () => this.transitionToDisplayMode('FAB'));
    this.iframeToHostMessageConsumer?.addListener('transition-to-fic', () => this.transitionToDisplayMode('FIC'));
    this.iframeToHostMessageConsumer?.addListener('transition-to-support-window', () =>
      this.transitionToDisplayMode('WINDOW')
    );
    this.iframeToHostMessageConsumer?.addListener('fic-input-focused', () => {
      if (!this.shouldHandleKeyboard()) {
        return;
      }

      this.inputFocused = true;
      this.keyboardOpenGuardUntil = Date.now() + KEYBOARD_OPEN_SETTLE_MS;
      this.handleInputFocus();
    });
  }

  private shouldHandleKeyboard() {
    return isIOSDevice() && !this.isDesktopMediaQuery.matches;
  }

  public async afterMount() {
    this.rebuildTransitionMap();
    this.setupPositionMediaQueryListeners();
    this.setupKeyboardFillListener();
    this.applyCurrentModeStyles();
    await this.handleFloatingInputCardTrigger();
  }

  public async destroy() {
    this.currentDisplayMode = 'HIDDEN';
    this.processingQueue = false;
    this.transitionQueue = [];
    this.transitionMap = undefined;
    this.mediaQueryListeners.forEach((cleanup) => cleanup());
    this.mediaQueryListeners = [];
    this.keyboardFillCleanup?.();
    this.keyboardFillCleanup = undefined;
    this.clearFocusTimeouts();
    this.inputFocused = false;
    this.syncParentScroll();
    await super.destroy();
  }

  protected actions: Actions = {
    ...this._actions,
    [ActionMethod.Hide]: () => {
      this.transitionToDisplayMode('HIDDEN');
    },
    [ActionMethod.Show]: async (params) => {
      if (!params || !params.displayMode) {
        if (this.currentDisplayMode === 'HIDDEN') {
          await this.transitionToPreviouslyVisibleDisplayMode();
        }
      } else {
        const { data: parsedDisplayMode, success, error } = ShowActionDisplayModeSchema.safeParse(params.displayMode);
        if (!success) {
          logger.warn('Invalid display mode provided. No action taken.', {
            displayMode: params.displayMode,
            parseIssues: error?.issues.map((issue) => issue.message),
          });
          return;
        }

        await this.transitionToDisplayMode(parsedDisplayMode);
      }
    },
    [ActionMethod.InitiateSimplrChat]: ({ message, context } = {}) => {
      this.transitionToDisplayMode('WINDOW');
      this.hostToIframeMessageProducer?.sendMessage({
        payload: {
          message,
          context,
        },
        type: 'simplr-initiate-chat',
      });
    },
  };

  private async handleInitialTransition() {
    const ficOptions = this.options?.['floating-input-card'];
    const initialMode = ficOptions?.initialDisplayMode ?? 'FIC';

    await this.transitionToDisplayMode(initialMode);
  }

  private async handleFloatingInputCardTrigger() {
    const ficOptions = this.options?.['floating-input-card'];
    if (ficOptions?.trigger === 'delay' && ficOptions.delayTimeInMs) {
      await this.handleDelayTrigger(ficOptions.delayTimeInMs);
    } else if (ficOptions?.trigger === 'scroll' && ficOptions.scrollThreshold) {
      await this.handleScrollTrigger(ficOptions.scrollThreshold);
    } else {
      await this.handleInitialTransition();
    }
  }

  private async handleDelayTrigger(delayTimeInMs: number) {
    setTimeout(async () => {
      await this.handleInitialTransition();
    }, delayTimeInMs);
  }

  private async handleScrollTrigger(scrollThreshold: number) {
    const checkScrollThreshold = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrolledPercentage = (scrollTop + windowHeight) / documentHeight;

      return scrolledPercentage >= scrollThreshold / 100;
    };

    if (checkScrollThreshold()) {
      await this.handleInitialTransition();
    } else {
      const handleScroll = async () => {
        if (checkScrollThreshold()) {
          await this.handleInitialTransition();
          window.removeEventListener('scroll', handleScroll);
        }
      };

      window.addEventListener('scroll', handleScroll);
    }
  }

  private applyStyles(styles: Record<string, unknown>) {
    if (!this.iframeElement) {
      return;
    }

    Object.assign(this.iframeElement.style, defaultIframeStyles);
    Object.assign(this.iframeElement.style, styles);
    const heightStr = String((styles as { height?: unknown }).height ?? '');

    if (heightStr.endsWith('px')) {
      const writtenHeight = parseInt(heightStr, 10);
      this.currentBaseHeightPx = Number.isNaN(writtenHeight) ? undefined : writtenHeight;
    } else {
      this.currentBaseHeightPx = undefined;
    }
    this.applyKeyboardFill();
  }

  private setupKeyboardFillListener() {
    if (typeof window === 'undefined' || !window.visualViewport || !this.shouldHandleKeyboard()) {
      return;
    }

    const vv = window.visualViewport;

    const handle = () => {
      if (!this.inputFocused) {
        return;
      }

      const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
      this.currentKeyboardInsetPx = keyboardHeight;
      this.maxKeyboardInsetPx = Math.max(this.maxKeyboardInsetPx, keyboardHeight);
      this.applyKeyboardFill();

      if (Date.now() >= this.keyboardOpenGuardUntil && this.isKeyboardClosed(keyboardHeight)) {
        this.handleInputBlur();
      }
    };

    vv.addEventListener('resize', handle);
    vv.addEventListener('scroll', handle);

    this.keyboardFillCleanup = () => {
      vv.removeEventListener('resize', handle);
      vv.removeEventListener('scroll', handle);
    };
  }

  private resetKeyboardTracking() {
    this.inputFocused = false;
    this.currentKeyboardInsetPx = 0;
    this.maxKeyboardInsetPx = 0;
    this.applyKeyboardFill();
  }

  private handleInputBlur() {
    if (!this.inputFocused) {
      return;
    }

    this.resetKeyboardTracking();
    this.syncParentScroll();
  }

  private isKeyboardClosed(keyboardHeight: number) {
    return this.maxKeyboardInsetPx > 0 && keyboardHeight < this.maxKeyboardInsetPx * KEYBOARD_CLOSED_INSET_RATIO;
  }

  private handleInputFocus() {
    if (typeof window === 'undefined') {
      return;
    }

    this.clearFocusTimeouts();

    this.focusTimeoutIds.push(
      setTimeout(() => {
        this.syncParentScroll();
      }, IOS_FOCUS_SETTLE_MS),

      setTimeout(() => {
        // No on-screen keyboard appeared (e.g. iPad with a hardware keyboard) - don't hold the scroll lock.
        if (this.inputFocused && this.maxKeyboardInsetPx === 0) {
          this.handleInputBlur();
        }
      }, KEYBOARD_OPEN_SETTLE_MS)
    );
  }

  private clearFocusTimeouts() {
    this.focusTimeoutIds.forEach((id) => clearTimeout(id));
    this.focusTimeoutIds = [];
  }

  private syncParentScroll(targetMode: DisplayModes = this.currentDisplayMode) {
    const shouldLock = this.inputFocused || (targetMode === 'WINDOW' && !this.isDesktopMediaQuery.matches);

    if (shouldLock === this.parentScrollLocked) {
      return;
    }

    this.parentScrollLocked = shouldLock;
    if (shouldLock) {
      this.preventParentScroll();
    } else {
      this.allowParentScroll();
    }
  }

  private applyKeyboardFill() {
    if (!this.iframeElement || this.currentBaseHeightPx == null) {
      return;
    }

    if (this.currentDisplayMode !== 'FIC' || this.isDesktopMediaQuery.matches) {
      this.iframeElement.style.height = `${this.currentBaseHeightPx}px`;
      return;
    }

    this.iframeElement.style.height = `${this.currentBaseHeightPx + this.currentKeyboardInsetPx}px`;
  }

  private async asyncSendAndAwaitMessage(sendMessage: HostToIframeMessage, awaitMessage: IframeToHostMessage) {
    const listener = this.iframeToHostMessageConsumer?.addAsyncListener(awaitMessage.type);
    this.hostToIframeMessageProducer?.sendMessage(sendMessage);
    await listener;
  }

  private enqueueTransitionFunction(transitionFunction: () => Promise<void>) {
    this.transitionQueue.push(transitionFunction);
    if (!this.processingQueue) {
      this.startQueueProcessor();
    }
  }
  private async startQueueProcessor() {
    this.processingQueue = true;
    while (this.transitionQueue.length > 0) {
      const transitionFunction = this.transitionQueue.shift();
      if (transitionFunction) {
        await transitionFunction();
      }
    }
    this.processingQueue = false;
  }

  private async transitionToDisplayMode(toMode: DisplayModes) {
    this.enqueueTransitionFunction(async () => {
      if (this.currentDisplayMode === toMode) {
        return;
      }

      if (this.currentDisplayMode === 'FIC' && toMode !== 'FIC') {
        this.resetKeyboardTracking();
      }

      this.syncParentScroll(toMode);

      if (!this.transitionMap) {
        const error = new Error('Transition map is not defined. Ensure the widget is initialized properly.');
        logger.error(error);
        throw error;
      }

      const fromTransition = this.transitionMap[this.currentDisplayMode];
      const toTransition = this.transitionMap[this.currentDisplayMode]?.[toMode];

      if (fromTransition && toTransition) {
        if (this.currentDisplayMode !== 'HIDDEN') {
          await this.asyncSendAndAwaitMessage(
            fromTransition.hide.startTransitionMessageType,
            fromTransition.hide.completeTransitionMessageType
          );
        }

        this.applyStyles(toTransition.styles(this.isDesktopMediaQuery.matches));
        await this.asyncSendAndAwaitMessage(
          toTransition.show.startTransitionMessageType,
          toTransition.show.completeTransitionMessageType
        );

        this.currentDisplayMode = toMode;
      } else {
        logger.warn(`No transition defined from ${this.currentDisplayMode} to ${toMode}`);
      }
    });
  }

  private async transitionToPreviouslyVisibleDisplayMode() {
    if (this.previouslyVisibleDisplayMode) {
      await this.transitionToDisplayMode(this.previouslyVisibleDisplayMode);
    } else {
      await this.transitionToDisplayMode('FIC');
    }
  }

  private getFicStyles(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getStylesFn: (params: GetStylesParams) => Record<string, any>,
    isDesktop = false,
    size: WidgetSize = 'large'
  ) {
    const ficOptions = this.options?.['floating-input-card'];
    const resolvedPosition = resolvePosition(ficOptions?.position);

    const withIntents = !!ficOptions?.topics?.length;

    return getStylesFn({
      ...resolvedPosition,
      size,
      withIntents,
    });
  }

  private get displayModeToResponsiveStyles() {
    const size =
      this.options?.['floating-input-card']?.size ?? this.optionOverrides?.['floating-input-card']?.size ?? 'large';
    return this.getFicStyles(getDisplayModeToResponsiveStyles, this.isDesktopMediaQuery.matches, size);
  }

  private get desktopSupportWindowStyles() {
    const size =
      this.options?.['floating-input-card']?.size ?? this.optionOverrides?.['floating-input-card']?.size ?? 'large';
    return this.getFicStyles(getDesktopSupportWindowStyles, true, size);
  }

  private get desktopDefaultStyles() {
    return this.getFicStyles(getDesktopDefaultStyles, true);
  }

  private get mobileSupportWindowStyles() {
    return this.getFicStyles(getMobileSupportWindowStyles);
  }

  private get mobileDefaultStyles() {
    return this.getFicStyles(getMobileDefaultStyles);
  }

  protected getIframeUrl() {
    return process.env.NX_PUBLIC_WIDGET_USE_SAME_ORIGIN
      ? this.buildUrl(this.loaderUrl.origin, 'widget-variant-fic')
      : this.buildUrl(process.env.NX_PUBLIC_FIC_IFRAME_ORIGIN as string);
  }

  private setupPositionMediaQueryListeners() {
    const position = this.options?.['floating-input-card']?.position;
    if (!position) {
      return;
    }

    const queries = Object.keys(position).filter((key) => key !== 'base');

    queries.forEach((query) => {
      const mql = window.matchMedia(query);

      const handler = () => {
        this.rebuildTransitionMap();
        this.applyCurrentModeStyles();
      };

      mql.addEventListener('change', handler);
      this.mediaQueryListeners.push(() => mql.removeEventListener('change', handler));
    });
  }

  private rebuildTransitionMap() {
    this.transitionMap = createTransitionMap({
      desktopDefaultStyles: this.desktopDefaultStyles,
      mobileDefaultStyles: this.mobileDefaultStyles,
      desktopSupportWindowStyles: this.desktopSupportWindowStyles,
      mobileSupportWindowStyles: this.mobileSupportWindowStyles,
    });
  }

  private applyCurrentModeStyles() {
    if (this.iframeElement) {
      this.applyStyles(
        this.isDesktopMediaQuery.matches
          ? this.displayModeToResponsiveStyles[this.currentDisplayMode].desktop
          : this.displayModeToResponsiveStyles[this.currentDisplayMode].mobile
      );
    }
  }
}
